import supabase from '../lib/supabase';

// Demo data for offline mode
const demoInvoices = [
  {
    id: 1,
    invoice_number: 'INV-1001',
    client_id: 1,
    client: {
      name: 'John Smith',
      business_name: 'TechCorp Ltd',
      email: 'john@techcorp.com',
      phone: '+1 (555) 123-4567',
      address: '123 Tech Street, Silicon Valley, CA 94000',
      gstin: '27AABCT1332L000',
      gst_type: 'B2B',
      billing_contact: {
        name: 'Sarah Johnson',
        email: 'billing@techcorp.com',
        phone: '+1 (555) 123-4568'
      }
    },
    invoice_type: 'recurring',
    status: 'sent',
    issue_date: '2024-01-15',
    due_date: '2024-02-14',
    services: [
      {
        id: 1,
        name: 'SEO Management',
        description: 'Monthly SEO optimization and reporting',
        quantity: 1,
        rate: 2000,
        amount: 2000,
        tax_rate: 18
      },
      {
        id: 2,
        name: 'PPC Campaign Management',
        description: 'Google Ads campaign setup and optimization',
        quantity: 30,
        rate: 50,
        amount: 1500,
        tax_rate: 18
      }
    ],
    subtotal: 3500,
    tax_breakdown: {
      cgst: 315,
      sgst: 315,
      igst: 0,
      total_tax: 630
    },
    total_amount: 4130,
    ad_spend: 5000,
    payment_terms: 'Net 30 days',
    notes: 'Thank you for your business!',
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-01-15T00:00:00Z'
  },
  {
    id: 2,
    invoice_number: 'INV-1002',
    client_id: 2,
    client: {
      name: 'Michael Brown',
      business_name: 'StartupXYZ',
      email: 'mike@startupxyz.com',
      phone: '+1 (555) 987-6543',
      address: '456 Innovation Ave, Austin, TX 78701',
      gstin: '07AABCS5555N000',
      gst_type: 'B2B',
      billing_contact: {
        name: 'Emily Davis',
        email: 'finance@startupxyz.com',
        phone: '+1 (555) 987-6544'
      }
    },
    invoice_type: 'one_time',
    status: 'draft',
    issue_date: '2024-01-20',
    due_date: '2024-02-19',
    services: [
      {
        id: 1,
        name: 'Website Development',
        description: 'Complete website redesign and development',
        quantity: 1,
        rate: 8000,
        amount: 8000,
        tax_rate: 18
      }
    ],
    subtotal: 8000,
    tax_breakdown: {
      cgst: 720,
      sgst: 720,
      igst: 0,
      total_tax: 1440
    },
    total_amount: 9440,
    ad_spend: 0,
    payment_terms: 'Net 45 days',
    notes: 'Project completion expected by month end.',
    created_at: '2024-01-20T00:00:00Z',
    updated_at: '2024-01-20T00:00:00Z'
  },
  {
    id: 3,
    invoice_number: 'INV-1003',
    client_id: 3,
    client: {
      name: 'Lisa Anderson',
      business_name: 'E-commerce Plus',
      email: 'lisa@ecommerceplus.com',
      phone: '+1 (555) 456-7890',
      address: '789 Commerce Blvd, New York, NY 10001',
      gstin: '',
      gst_type: 'B2C',
      billing_contact: {
        name: 'David Miller',
        email: 'accounts@ecommerceplus.com',
        phone: '+1 (555) 456-7891'
      }
    },
    invoice_type: 'ad_spend',
    status: 'overdue',
    issue_date: '2024-01-10',
    due_date: '2024-02-09',
    services: [
      {
        id: 1,
        name: 'Social Media Marketing',
        description: 'Facebook and Instagram campaign management',
        quantity: 1,
        rate: 1200,
        amount: 1200,
        tax_rate: 0
      }
    ],
    subtotal: 1200,
    tax_breakdown: {
      cgst: 0,
      sgst: 0,
      igst: 0,
      total_tax: 0
    },
    total_amount: 1200,
    ad_spend: 3500,
    payment_terms: 'Net 30 days',
    notes: 'No GST applicable for B2C services.',
    created_at: '2024-01-10T00:00:00Z',
    updated_at: '2024-01-10T00:00:00Z'
  }
];

const serviceTemplates = [
  {
    id: 1,
    name: 'SEO Management',
    description: 'Monthly SEO optimization and reporting',
    default_rate: 2000,
    billing_type: 'fixed',
    tax_rate: 18,
    category: 'SEO'
  },
  {
    id: 2,
    name: 'PPC Campaign Management',
    description: 'Google Ads campaign setup and optimization',
    default_rate: 50,
    billing_type: 'hourly',
    tax_rate: 18,
    category: 'PPC'
  },
  {
    id: 3,
    name: 'Social Media Marketing',
    description: 'Social media strategy and content creation',
    default_rate: 1500,
    billing_type: 'fixed',
    tax_rate: 18,
    category: 'Social Media'
  },
  {
    id: 4,
    name: 'Web Development',
    description: 'Website design and development services',
    default_rate: 100,
    billing_type: 'hourly',
    tax_rate: 18,
    category: 'Development'
  },
  {
    id: 5,
    name: 'Content Marketing',
    description: 'Content creation and marketing strategy',
    default_rate: 80,
    billing_type: 'hourly',
    tax_rate: 18,
    category: 'Content'
  },
  {
    id: 6,
    name: 'Analytics & Reporting',
    description: 'Monthly analytics and performance reporting',
    default_rate: 800,
    billing_type: 'fixed',
    tax_rate: 18,
    category: 'Analytics'
  }
];

class InvoiceService {
  constructor() {
    this.isOnline = false;
    this.checkConnection();
  }

  async checkConnection() {
    try {
      const { data, error } = await supabase.from('invoices_2024').select('count', { count: 'exact' });
      this.isOnline = !error;
    } catch (error) {
      this.isOnline = false;
    }
  }

  generateInvoiceNumber() {
    const currentYear = new Date().getFullYear();
    const timestamp = Date.now().toString().slice(-4);
    return `INV-${currentYear}-${timestamp}`;
  }

  calculateGST(amount, gstRate, clientLocation = 'same_state') {
    if (gstRate === 0) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        total_tax: 0
      };
    }

    const taxAmount = (amount * gstRate) / 100;
    
    if (clientLocation === 'different_state') {
      return {
        cgst: 0,
        sgst: 0,
        igst: taxAmount,
        total_tax: taxAmount
      };
    } else {
      return {
        cgst: taxAmount / 2,
        sgst: taxAmount / 2,
        igst: 0,
        total_tax: taxAmount
      };
    }
  }

  calculateInvoiceTotal(services, clientGstType = 'B2B', clientLocation = 'same_state') {
    const subtotal = services.reduce((sum, service) => sum + (service.amount || 0), 0);
    
    let totalTax = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    let igstTotal = 0;

    services.forEach(service => {
      const taxRate = clientGstType === 'B2C' ? 0 : (service.tax_rate || 0);
      const gstCalc = this.calculateGST(service.amount, taxRate, clientLocation);
      
      cgstTotal += gstCalc.cgst;
      sgstTotal += gstCalc.sgst;
      igstTotal += gstCalc.igst;
      totalTax += gstCalc.total_tax;
    });

    return {
      subtotal,
      tax_breakdown: {
        cgst: Math.round(cgstTotal * 100) / 100,
        sgst: Math.round(sgstTotal * 100) / 100,
        igst: Math.round(igstTotal * 100) / 100,
        total_tax: Math.round(totalTax * 100) / 100
      },
      total_amount: Math.round((subtotal + totalTax) * 100) / 100
    };
  }

  async getAllInvoices() {
    if (!this.isOnline) {
      return { data: demoInvoices, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('invoices_2024')
        .select(`
          *,
          client:clients_billing_2024(*)
        `)
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      return { data: demoInvoices, error: null };
    }
  }

  async getInvoice(id) {
    if (!this.isOnline) {
      const invoice = demoInvoices.find(inv => inv.id === parseInt(id));
      return { data: invoice, error: invoice ? null : 'Invoice not found' };
    }

    try {
      const { data, error } = await supabase
        .from('invoices_2024')
        .select(`
          *,
          client:clients_billing_2024(*)
        `)
        .eq('id', id)
        .single();

      return { data, error };
    } catch (error) {
      const invoice = demoInvoices.find(inv => inv.id === parseInt(id));
      return { data: invoice, error: invoice ? null : 'Invoice not found' };
    }
  }

  async createInvoice(invoiceData) {
    const invoiceNumber = this.generateInvoiceNumber();
    const calculations = this.calculateInvoiceTotal(
      invoiceData.services,
      invoiceData.client?.gst_type,
      invoiceData.client_location
    );

    const newInvoice = {
      ...invoiceData,
      invoice_number: invoiceNumber,
      ...calculations,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    if (!this.isOnline) {
      newInvoice.id = Math.max(...demoInvoices.map(inv => inv.id)) + 1;
      demoInvoices.unshift(newInvoice);
      return { data: newInvoice, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('invoices_2024')
        .insert([newInvoice])
        .select(`
          *,
          client:clients_billing_2024(*)
        `)
        .single();

      return { data, error };
    } catch (error) {
      newInvoice.id = Math.max(...demoInvoices.map(inv => inv.id)) + 1;
      demoInvoices.unshift(newInvoice);
      return { data: newInvoice, error: null };
    }
  }

  async updateInvoice(id, invoiceData) {
    const calculations = this.calculateInvoiceTotal(
      invoiceData.services,
      invoiceData.client?.gst_type,
      invoiceData.client_location
    );

    const updatedData = {
      ...invoiceData,
      ...calculations,
      updated_at: new Date().toISOString()
    };

    if (!this.isOnline) {
      const index = demoInvoices.findIndex(inv => inv.id === parseInt(id));
      if (index !== -1) {
        demoInvoices[index] = { ...demoInvoices[index], ...updatedData };
        return { data: demoInvoices[index], error: null };
      }
      return { data: null, error: 'Invoice not found' };
    }

    try {
      const { data, error } = await supabase
        .from('invoices_2024')
        .update(updatedData)
        .eq('id', id)
        .select(`
          *,
          client:clients_billing_2024(*)
        `)
        .single();

      return { data, error };
    } catch (error) {
      const index = demoInvoices.findIndex(inv => inv.id === parseInt(id));
      if (index !== -1) {
        demoInvoices[index] = { ...demoInvoices[index], ...updatedData };
        return { data: demoInvoices[index], error: null };
      }
      return { data: null, error: 'Invoice not found' };
    }
  }

  async deleteInvoice(id) {
    if (!this.isOnline) {
      const index = demoInvoices.findIndex(inv => inv.id === parseInt(id));
      if (index !== -1) {
        const deleted = demoInvoices.splice(index, 1)[0];
        return { data: deleted, error: null };
      }
      return { data: null, error: 'Invoice not found' };
    }

    try {
      const { data, error } = await supabase
        .from('invoices_2024')
        .delete()
        .eq('id', id)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      const index = demoInvoices.findIndex(inv => inv.id === parseInt(id));
      if (index !== -1) {
        const deleted = demoInvoices.splice(index, 1)[0];
        return { data: deleted, error: null };
      }
      return { data: null, error: 'Invoice not found' };
    }
  }

  async getServiceTemplates() {
    return { data: serviceTemplates, error: null };
  }

  async updateInvoiceStatus(id, status) {
    return await this.updateInvoice(id, { status });
  }

  async getInvoicesByClient(clientId) {
    const { data: invoices } = await this.getAllInvoices();
    return {
      data: invoices.filter(invoice => invoice.client_id === parseInt(clientId)),
      error: null
    };
  }

  async getInvoicesByStatus(status) {
    const { data: invoices } = await this.getAllInvoices();
    return {
      data: invoices.filter(invoice => invoice.status === status),
      error: null
    };
  }

  async generateRecurringInvoices() {
    // This would typically run as a scheduled job
    const { data: invoices } = await this.getAllInvoices();
    const recurringInvoices = invoices.filter(inv => inv.invoice_type === 'recurring');
    
    // Logic to generate new invoices based on recurring templates
    // This is a simplified version
    return { data: [], error: null };
  }
}

export default new InvoiceService();